<template>
  <footer class="bg-t3-bg-dark pt-16 sm:pt-24 pb-12 px-4 sm:px-6 lg:px-20 font-t3-display" role="contentinfo">
    <div class="max-w-7xl mx-auto">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16 mb-16 md:mb-20">
        <div>
          <div class="flex items-center gap-2 text-t3-primary mb-6">
            <button
              v-if="canOpenOwnerLogoModal"
              type="button"
              class="min-h-[44px] min-w-[44px] shrink-0 rounded overflow-hidden bg-t3-accent-dark inline-flex items-center justify-center border border-white/10 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary focus-visible:ring-offset-2 focus-visible:ring-offset-t3-bg-dark"
              :aria-label="$t('public.ownerEditLogoAria')"
              @click="openOwnerLogoModal"
            >
              <img
                v-if="logoUrl"
                :src="logoUrl"
                :alt="restaurantName || 'Restaurant'"
                class="w-10 h-10 object-contain"
              >
              <span v-else class="material-symbols-outlined text-2xl text-t3-primary" aria-hidden="true">restaurant</span>
            </button>
            <div v-else class="w-10 h-10 shrink-0 rounded overflow-hidden bg-t3-accent-dark flex items-center justify-center border border-white/10">
              <img
                v-if="logoUrl"
                :src="logoUrl"
                :alt="restaurantName || 'Restaurant'"
                class="w-full h-full object-contain"
              >
              <span v-else class="material-symbols-outlined text-2xl text-t3-primary" aria-hidden="true">restaurant</span>
            </div>
            <h2 class="text-xl font-bold tracking-tight text-slate-100">{{ restaurantName || 'Restaurant' }}</h2>
          </div>
          <p class="text-slate-400 leading-relaxed mb-6 text-sm">
            {{ footerTagline || $t('public.t3FooterTagline') }}
          </p>
        </div>
        <div>
          <h4 class="text-white font-bold mb-6 text-lg uppercase tracking-widest text-sm">{{ $t('public.t3QuickLinks') }}</h4>
          <ul class="space-y-4 text-slate-400 text-sm">
            <li><a href="#menu" class="hover:text-t3-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary rounded">{{ viewListLabel || $t('public.t3NavMenu') }}</a></li>
            <li v-if="feedbackAvailable"><a href="#reviews" class="hover:text-t3-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary rounded">{{ $t('public.t3NavReviews') }}</a></li>
          </ul>
        </div>
        <div>
          <h4 class="text-white font-bold mb-6 text-lg uppercase tracking-widest text-sm">{{ $t('public.contactUs') }}</h4>
          <p class="text-slate-400 text-sm">{{ $t('public.t3FooterContact') }}</p>
        </div>
      </div>
      <div class="border-t border-white/5 pt-12 text-center space-y-4">
        <div class="flex flex-wrap items-center justify-center gap-4 min-h-[44px]">
          <button
            type="button"
            class="text-slate-400 hover:text-slate-200 text-sm min-h-[44px] min-w-[44px] inline-flex items-center justify-center -m-2 p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary rounded"
            @click="openLegalModal('terms')"
          >
            {{ $t('app.termsOfService') }}
          </button>
          <button
            type="button"
            class="text-slate-400 hover:text-slate-200 text-sm min-h-[44px] min-w-[44px] inline-flex items-center justify-center -m-2 p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary rounded"
            @click="openLegalModal('privacy')"
          >
            {{ $t('app.privacyPolicy') }}
          </button>
          <button
            type="button"
            class="text-slate-400 hover:text-slate-200 text-sm min-h-[44px] min-w-[44px] inline-flex items-center justify-center -m-2 p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary rounded"
            @click="openCookiePrefs"
          >
            {{ $t('landing.footer.cookies') }}
          </button>
        </div>
        <p class="text-slate-500 text-sm uppercase tracking-widest">
          © {{ currentYear }} {{ restaurantName || 'Restaurant' }}
        </p>
        <PublicFooterCredits v-if="showCredits" class="text-slate-500 [&_a]:text-slate-500 [&_span]:text-slate-500 hover:[&_a]:text-slate-400" compact />
      </div>
    </div>
    <LegalContentModal
      :open="legalModalOpen"
      :type="legalModalType"
      :locale="modalLocale"
      @close="closeLegalModal"
    />
  </footer>
</template>

<script setup>
import { ref, computed } from 'vue'
import LegalContentModal from '@/components/legal/LegalContentModal.vue'
import PublicFooterCredits from '@/components/public/PublicFooterCredits.vue'
import { usePublicOwnerLogoModal } from '@/composables/usePublicOwnerLogoModal'
import { useCookieConsentStore } from '@/stores/sitePrefs'

const { canOpen: canOpenOwnerLogoModal, openOwnerLogoModal } = usePublicOwnerLogoModal()
const cookieConsentStore = useCookieConsentStore()

defineProps({
  restaurantName: { type: String, default: '' },
  logoUrl: { type: String, default: '' },
  showCredits: { type: Boolean, default: true },
  modalLocale: { type: String, default: 'en' },
  viewListLabel: { type: String, default: '' },
  footerTagline: { type: String, default: '' },
  /** Growth / Pro / VIP: owner has feedback feature; hide #reviews link for Starter (matches API feedback_available). */
  feedbackAvailable: { type: Boolean, default: false },
})

const legalModalOpen = ref(false)
const legalModalType = ref('terms')

const currentYear = computed(() => new Date().getFullYear())

function openLegalModal(type) {
  legalModalType.value = type
  legalModalOpen.value = true
}

function closeLegalModal() {
  legalModalOpen.value = false
}

function openCookiePrefs() {
  cookieConsentStore.openPrefs()
}
</script>
